/**
 * Giao diện thanh mục tiêu donate (overlay OBS, thẻ trên trang creator, xem trước trong cài đặt).
 *
 * BE lưu nguyên chuỗi JSON (≤ 1000 ký tự) và trả lại y nguyên, nên mọi kiểm tra/giá trị mặc định
 * nằm ở đây. Chuỗi hỏng hay trường lạ đều bị bỏ qua để overlay đang chạy trên live không bao giờ
 * vỡ vì một bản lưu sai.
 */

export type DonateGoalPreset = 'glass' | 'light' | 'gradient' | 'neon' | 'clear';

export interface DonateGoalTheme {
  v?: 1;
  preset?: DonateGoalPreset;
  barFrom?: string;
  barTo?: string;
  bg?: string;
  /** 0-100 */
  bgOpacity?: number;
  text?: string;
  accent?: string;
}

export interface ResolvedDonateGoalTheme {
  preset: DonateGoalPreset;
  barFrom: string;
  barTo: string;
  bg: string;
  bgOpacity: number;
  text: string;
  accent: string;
}

export const DEFAULT_DONATE_GOAL_PRESET: DonateGoalPreset = 'glass';

export const DONATE_GOAL_PRESETS: Array<{ id: DonateGoalPreset; label: string }> = [
  { id: 'glass', label: 'Kính tối' },
  { id: 'light', label: 'Sáng' },
  { id: 'gradient', label: 'Chuyển màu' },
  { id: 'neon', label: 'Neon' },
  { id: 'clear', label: 'Trong suốt' },
];

export const DONATE_GOAL_PRESET_DEFAULTS: Record<
  DonateGoalPreset,
  Omit<ResolvedDonateGoalTheme, 'preset'>
> = {
  glass: {
    barFrom: '#3B82F6',
    barTo: '#8B5CF6',
    bg: '#0F172A',
    bgOpacity: 75,
    text: '#FFFFFF',
    accent: '#34D399',
  },
  light: {
    barFrom: '#3B82F6',
    barTo: '#8B5CF6',
    bg: '#FFFFFF',
    bgOpacity: 92,
    text: '#0F172A',
    accent: '#059669',
  },
  gradient: {
    barFrom: '#FFFFFF',
    barTo: '#DBEAFE',
    bg: '#0057FF',
    bgOpacity: 100,
    text: '#FFFFFF',
    accent: '#A7F3D0',
  },
  neon: {
    barFrom: '#22D3EE',
    barTo: '#E879F9',
    bg: '#05060A',
    bgOpacity: 88,
    text: '#F0F9FF',
    accent: '#22D3EE',
  },
  clear: {
    barFrom: '#3B82F6',
    barTo: '#8B5CF6',
    bg: '#0F172A',
    bgOpacity: 0,
    text: '#FFFFFF',
    accent: '#34D399',
  },
};

/** Điểm cuối dải màu nền của mẫu "Chuyển màu" khi streamer chưa tự chọn màu nền. */
export const GRADIENT_PRESET_BG_TO = '#7C3AED';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const PRESET_IDS = DONATE_GOAL_PRESETS.map((p) => p.id);
const COLOR_KEYS = ['barFrom', 'barTo', 'bg', 'text', 'accent'] as const;

export const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && HEX_RE.test(value);

export const isDonateGoalPreset = (value: unknown): value is DonateGoalPreset =>
  typeof value === 'string' && (PRESET_IDS as string[]).includes(value);

/** Giữ lại đúng các trường hợp lệ, bỏ hết phần còn lại. */
export const sanitizeDonateGoalTheme = (input: unknown): DonateGoalTheme => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const src = input as Record<string, unknown>;
  const out: DonateGoalTheme = {};
  if (isDonateGoalPreset(src.preset)) out.preset = src.preset;
  COLOR_KEYS.forEach((key) => {
    if (isHexColor(src[key])) out[key] = (src[key] as string).toUpperCase();
  });
  const op = src.bgOpacity;
  if (typeof op === 'number' && Number.isFinite(op)) {
    out.bgOpacity = Math.round(Math.max(0, Math.min(100, op)));
  }
  return out;
};

/** Đọc chuỗi theme từ BE. Rỗng, null hay JSON hỏng đều trả về {} (tức giao diện mặc định). */
export const parseDonateGoalTheme = (raw?: string | null): DonateGoalTheme => {
  if (!raw || typeof raw !== 'string') return {};
  try {
    return sanitizeDonateGoalTheme(JSON.parse(raw));
  } catch {
    return {};
  }
};

/** Chuỗi gửi lên BE (luôn kèm v:1). */
export const serializeDonateGoalTheme = (theme: DonateGoalTheme): string =>
  JSON.stringify({ v: 1, ...sanitizeDonateGoalTheme(theme) });

const withoutPreset = (theme: DonateGoalTheme) => {
  const rest: Partial<Omit<ResolvedDonateGoalTheme, 'preset'>> = {};
  COLOR_KEYS.forEach((key) => {
    if (theme[key]) rest[key] = theme[key];
  });
  if (theme.bgOpacity !== undefined) rest.bgOpacity = theme.bgOpacity;
  return rest;
};

/** Gộp mẫu có sẵn với các màu streamer tự chọn: trường nào có thì ghi đè mặc định của mẫu. */
export const resolveDonateGoalTheme = (theme?: DonateGoalTheme | null): ResolvedDonateGoalTheme => {
  const clean = sanitizeDonateGoalTheme(theme);
  const preset = clean.preset || DEFAULT_DONATE_GOAL_PRESET;
  return { preset, ...DONATE_GOAL_PRESET_DEFAULTS[preset], ...withoutPreset(clean) };
};

export const hexToRgba = (hex: string, alpha: number) => {
  const safe = isHexColor(hex) ? hex : '#000000';
  const r = parseInt(safe.slice(1, 3), 16);
  const g = parseInt(safe.slice(3, 5), 16);
  const b = parseInt(safe.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, alpha))})`;
};

/** Độ sáng tương đối 0..1 (để chọn sọc tối hay sáng trên thanh). */
export const hexLuminance = (hex: string) => {
  const safe = isHexColor(hex) ? hex : '#000000';
  const ch = [1, 3, 5].map((i) => {
    const c = parseInt(safe.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
};

/** Làm tối màu hex theo tỉ lệ 0..1. */
export const darkenHex = (hex: string, amount: number) => {
  const safe = isHexColor(hex) ? hex : '#000000';
  const k = 1 - Math.max(0, Math.min(1, amount));
  const part = (i: number) =>
    Math.round(parseInt(safe.slice(i, i + 2), 16) * k)
      .toString(16)
      .padStart(2, '0');
  return `#${part(1)}${part(3)}${part(5)}`.toUpperCase();
};
